import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import "./styles/Home.css";
import { useAuth } from "../providers/AuthProvider";
import { useMsal } from "@azure/msal-react";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import ProjectDetails from "./ProjectDetails";
import Loader from "./Loader";
import { useLocation, useNavigate } from "react-router-dom";
import SurveyLoader from "./SurveyLoader";
import useDebounce from "../hooks/useDebounce";
import { FaSearch, FaTimes, FaSync, FaSpinner } from "react-icons/fa";

// Memoized button component
const ProjectButton = React.memo(({ project, onClick, isSelected }) => (
  <button className={`home-project-button ${isSelected ? 'selected' : ''}`} onClick={onClick}>
    <span>{project.project_name || project.name}</span>
    <span className="project-id">ID: {project.id}</span>
  </button>
));
ProjectButton.displayName = "ProjectButton";

function Home() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Refresh loading state
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [file, setFile] = useState(null);
  const [qnrFile, setQnrFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);

  const [contents, setContents] = useState([]);
  const [error, setError] = useState(null);
  const [includeExtraData, setIncludeExtraData] = useState(false);
  const [titles, setTitles] = useState([]);
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");

  // Progress tracking states
  const [processingMessages, setProcessingMessages] = useState([]);
  const [isProcessingSurvey, setIsProcessingSurvey] = useState(false);
  const [messageInterval, setMessageInterval] = useState(null);
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);

  const [showModelDialog, setShowModelDialog] = useState(false);
  const [selectedModel, setSelectedModel] = useState("gemini");
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  // Search state for My Projects sidebar
  const [projectSearchQuery, setProjectSearchQuery] = useState("");

  // Cache states
  const [clientsCache, setClientsCache] = useState(null);
  const [clientsTimestamp, setClientsTimestamp] = useState(0);
  const [projectsCache, setProjectsCache] = useState({});

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  const {
    setOriginalDataRows,
    selectedProjectDetails,
    setSelectedProjectDetails,
    loading,
    setLoading,
    setBanners,
    setdataGroup,
    dataGroup,
    projectId,
    setProjectId,
    setWeightingData,
    setWeightingStats,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    setVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    setSdataGroup,
    powner,
    setPowner,
    setSurveyFileName,
    setSurveyName,
  } = useContext(VariableContext);
  const { selectedBanner, setselectedBanner } = useContext(FetchingFreq);
  const location = useLocation();
  const { toast } = useToast();

  const { setUser, user } = useAuth();
  const { instance, accounts } = useMsal();
  const isSavFile = useMemo(() => {
    return file?.name?.toLowerCase().endsWith(".sav");
  }, [file]);

  const debouncedClientSearch = useDebounce(clientSearchTerm, 300);
  const debouncedProjectSearch = useDebounce(projectSearchTerm, 300);
  const debouncedProjectQuery = useDebounce(projectSearchQuery, 300);
  const [uploadMode, setUploadMode] = useState("truncate");

  // Filter projects based on search query - Enhanced to search by owner, client, project name
  const filteredProjectsBySearch = useMemo(() => {
    const allProjects = user?.user?.projects || [];
    if (!debouncedProjectQuery) return allProjects;

    const query = debouncedProjectQuery.toLowerCase();
    return allProjects.filter(project => {
      // Search by Project Name
      if (project.project_name?.toLowerCase().includes(query)) return true;
      // Search by Client Name
      if (project.client_name?.toLowerCase().includes(query)) return true;
      // Search by Project ID
      if (project.id?.toString().includes(query)) return true;
      // Search by Owner Email
      if (project.owner?.toLowerCase().includes(query)) return true;
      // Search by Owner Name (without domain)
      const ownerName = project.owner?.split('@')[0]?.toLowerCase() || '';
      if (ownerName.includes(query)) return true;
      return false;
    });
  }, [user?.user?.projects, debouncedProjectQuery]);

  // Sorted and filtered projects
  const sortedProjects = useMemo(() => {
    return [...filteredProjectsBySearch].sort((a, b) => b.id - a.id);
  }, [filteredProjectsBySearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".home-autocomplete-wrapper")) {
        setShowClientDropdown(false);
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Refresh projects on mount
  useEffect(() => {
    if (user?.user?.email) {
      refreshUserProjects();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [messageInterval]);

  const fetchContents = useCallback(async () => {
    try {
      const { data } = await apiService.post("/getListFolder", {
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      setContents(data.value);
    } catch (error) {
      setError(error.message);
    }
  }, [selectedProject, selectedClient]);

  const refreshUserProjects = useCallback(async () => {
    // Don't refresh if already refreshing
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      const account = accounts[0];
      if (!account) {
        console.error("No active account found");
        showToastMessage("Session expired. Please log in again.");
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        account: account,
        scopes: ["openid", "profile", "email"],
      });

      const { data } = await apiService.post("/loginOffice", {
        id_token: tokenResponse.idToken,
      });
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
      showToastMessage("Projects refreshed successfully!", "success");
    } catch (error) {
      console.error("Failed to refresh user projects:", error);
      showToastMessage("Failed to refresh projects list");
    } finally {
      setIsRefreshing(false);
    }
  }, [setUser, instance, accounts, isRefreshing]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      showToastMessage("Please select a file before uploading.");
      return;
    }

    let clientData = selectedClient;
    let projectData = selectedProject;

    if (!clientData || !clientData.name) {
      showToastMessage("Please select or enter a client name.");
      return;
    }

    if (!projectData || !projectData.name) {
      showToastMessage("Please select or enter a project name.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user", JSON.stringify(user));
    formData.append("project", JSON.stringify(projectData));
    formData.append("client", JSON.stringify(clientData));
    formData.append("includeExtraData", includeExtraData ? true : false);
    formData.append("uploadMode", uploadMode);

    setLoading(true);
    setIsUploading(true);

    try {
      const { data } = await apiService.upload("/upload", formData);
      let createdProjectId = data.project_id;
      let finalDataGroup = data.qgroup;

      const sortedKeys = Object.keys(finalDataGroup).sort(
        (a, b) => finalDataGroup[a].order - finalDataGroup[b].order
      );
      let sortedGroup = sortedKeys.reduce((acc, key) => {
        acc[key] = finalDataGroup[key];
        return acc;
      }, {});

      let finalDetails = {
        qgroup_json: sortedGroup,
        id: createdProjectId,
        client: data.client || clientData,
        project: data.project || projectData,
        filename: data.filename,
        uploaded_time: data.uploaded_time,
        has_database: data.has_database,
      };

      if (qnrFile) {
        try {
          const qnrFormData = new FormData();
          qnrFormData.append("file", qnrFile);
          qnrFormData.append("projectId", createdProjectId);
          qnrFormData.append("client", JSON.stringify(data.client || clientData));
          qnrFormData.append("project", JSON.stringify(data.project || projectData));

          const qnrResponse = await apiService.upload("/upload-qnr", qnrFormData);

          if (qnrResponse.data.success) {
            finalDetails.qnr_filename = qnrFile.name;

            const { data: validateData } = await apiService.post(
              `/project/${createdProjectId}/validate_and_update`,
              { data_group: finalDataGroup, qnr_filename: qnrFile.name }
            );
            if (validateData && validateData.success) {
              finalDataGroup = validateData.updated_data_group;
              const valSortedKeys = Object.keys(finalDataGroup).sort(
                (a, b) => finalDataGroup[a].order - finalDataGroup[b].order
              );
              sortedGroup = valSortedKeys.reduce((acc, key) => {
                acc[key] = finalDataGroup[key];
                return acc;
              }, {});
              finalDetails.qgroup_json = sortedGroup;
              showToastMessage(`Success! SPSS uploaded, QNR uploaded, and ${validateData.stats?.changed_variables || 0} variables updated.`);
            } else {
              showToastMessage(`SPSS/QNR uploaded, but AI validation failed: ${validateData?.error || "Unknown error"}`);
            }
          } else {
            showToastMessage(`SPSS uploaded, but QNR upload failed: ${qnrResponse.data.message}`);
          }
        } catch (qnrError) {
          console.error("QNR Upload/Validation Error:", qnrError);
          showToastMessage(`SPSS uploaded successfully, but QNR processing failed: ${qnrError.message}`);
        }
      } else {
        showToastMessage(data.message || "File uploaded successfully.");
      }

      const extractedTitles = Object.keys(sortedGroup);
      setProjectId(createdProjectId);
      setTitles(extractedTitles);
      setVariables(extractedTitles);
      setSelectedProjectDetails(finalDetails);
      setPowner(user.user.email);
      setdataGroup(sortedGroup);
      setShowForm(false);
      setFile(null);
      setQnrFile(null);

      fetchContents();
      await refreshUserProjects();

    } catch (error) {
      console.error("Upload error:", error);
      showToastMessage(error.message || "An error occurred while uploading the file.");
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  }, [
    file, qnrFile, selectedClient, selectedProject, user, includeExtraData,
    setLoading, setProjectId, setVariables, setdataGroup, fetchContents,
    refreshUserProjects, setSelectedProjectDetails, setPowner
  ]);

  const handleSurvey = useCallback(async () => {
    if (!file) {
      showToastMessage("Please select a file before uploading.");
      return;
    }

    let clientData = selectedClient;
    let projectData = selectedProject;

    if (!clientData || !clientData.name) {
      showToastMessage("Please select or enter a client name.");
      return;
    }

    if (!projectData || !projectData.name) {
      showToastMessage("Please select or enter a project name.");
      return;
    }

    const formData = new FormData();
    formData.append("questionnaire", file);
    if (excelFile) {
      formData.append("excel", excelFile);
    }
    formData.append("user", JSON.stringify(user));
    formData.append("project", JSON.stringify(projectData));
    formData.append("client", JSON.stringify(clientData));
    formData.append("includeExtraData", includeExtraData);

    const modelToUse = excelFile ? "gemini" : selectedModel;
    formData.append("model", modelToUse);

    setIsProcessingSurvey(true);
    setSurveyFileName(file.name);
    setSurveyName(file.name.replace(/\.[^/.]+$/, ""));

    try {
      const { data } = await apiService.upload("/surveyuploadnew", formData);

      if (data.fallback_used) {
        showToastMessage(
          `✅ Survey processed successfully using ${data.model_used.toUpperCase()} model (${data.original_model.toUpperCase()} tokens exhausted)`
        );
      }

      setSdataGroup(data.sqgroup);
      setProjectId(data.project_id);
      setSelectedProjectDetails({
        sqgroup_json: data.sqgroup,
        id: data.project_id,
        client: data.client || clientData,
        project: data.project || projectData,
        filename: data.filename,
        uploaded_time: data.uploaded_time,
        has_database: data.has_database,
      });

      await refreshUserProjects();
      setIsProcessingSurvey(false);
      setTimeout(() => {
        navigate("/surveybuilder");
      }, 50);
    } catch (error) {
      showToastMessage("An error occurred while uploading the file.");
      setIsProcessingSurvey(false);
    }
  }, [
    file, excelFile, selectedClient, selectedProject, user, includeExtraData,
    selectedModel, setSdataGroup, setProjectId, refreshUserProjects, navigate
  ]);

  const showToastMessage = useCallback((message, type = 'info') => {
    const fn = toast[type] ?? toast.info;
    fn(message);
  }, [toast]);

  const getProjectList = useCallback(async (client) => {
    let clientId;
    if (typeof client === "object" && client !== null) {
      clientId = client.id;
    } else {
      clientId = client;
    }

    if (!clientId) {
      console.error("No client ID provided");
      return;
    }

    const now = Date.now();
    if (projectsCache[clientId] && now - projectsCache[clientId].timestamp < CACHE_DURATION) {
      setProjects(projectsCache[clientId].data);
      return;
    }

    try {
      const { data } = await apiService.post("/projects", { clientId });
      setProjects(data);
      setProjectsCache((prev) => ({
        ...prev,
        [clientId]: { data: data, timestamp: now },
      }));
    } catch (error) {
      console.error("Error fetching project list:", error);
      showToastMessage("An error occurred while fetching ProjectList.");
    }
  }, [projectsCache, showToastMessage]);

  const fetchClientsProjects = useCallback(async () => {
    const now = Date.now();
    if (clientsCache && now - clientsTimestamp < CACHE_DURATION) {
      setClients(clientsCache);
      return;
    }

    try {
      setLoading(true);
      const { data } = await apiService.get("/clients");
      setClients(data);
      setClientsCache(data);
      setClientsTimestamp(now);
    } catch (error) {
      console.error("Failed to fetch clients and projects:", error);
    } finally {
      setLoading(false);
    }
  }, [clientsCache, clientsTimestamp, setLoading]);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    if (!selectedFile?.name?.toLowerCase().endsWith(".sav")) {
      setIncludeExtraData(false);
      setQnrFile(null);
    }
  }, []);

  const handleQnrFileChange = useCallback((e) => {
    setQnrFile(e.target.files[0]);
  }, []);

  const handleExcelChange = useCallback((e) => {
    setExcelFile(e.target.files[0]);
  }, []);

  const handleProjectClick = useCallback((project) => {
    setSelectedProjectId(project.id);
    setLoading(true);
    apiService.get(`/project/${project.id}/files`)
      .then(({ data }) => {
        if (data.length > 0 && (data[0].qgroup_json || data[0].sqgroup_json)) {
          const qgroup = data[0].qgroup_json;
          if (qgroup) {
            const sortedKeys = Object.keys(qgroup)
              .filter((key) => qgroup[key] != null)
              .sort((a, b) => (qgroup[a].order ?? 0) - (qgroup[b].order ?? 0));
            const sortedGroup = sortedKeys.reduce((acc, key) => {
              acc[key] = qgroup[key];
              return acc;
            }, {});
            setdataGroup(sortedGroup || []);
          }
          setProjectId(project.id);
          setSelectedClient(data[0].client);
          setSelectedProject(data[0].project);
          setSdataGroup(data[0].sqgroup_json || []);
          setBanners(data[0].banner_data);
          setPowner(project.owner);
          setselectedBanner([]);
          setWeightingData({});
          setSavedWeightings(data[0].weighting);
          if (data[0].weighting) {
            setWeightingStats({ ...data[0].weighting["weight"].stats });
            setcurrentSelectedVariablesset(
              Object.keys(data[0].weighting["weight"]["weightedCounts"] || {})
            );
          }

          setShowForm(false);
          setSelectedProjectDetails(data[0]);

          const tables = data[0]?.tables || {};
          const baseRows = tables.table_single?.length > 0 ? tables.table_single : [];
          const numericRows = tables.table_numeric || [];
          const openEndedRows = tables.table_openended || [];

          const numericMap = Object.fromEntries(numericRows.map((r) => [r.id, r]));
          const openMap = Object.fromEntries(openEndedRows.map((r) => [r.id, r]));

          const mergedRows = baseRows.map((row) => ({
            ...row,
            ...(numericMap[row.id] || {}),
            ...(openMap[row.id] || {}),
          }));

          setOriginalDataRows(mergedRows);
        }
      })
      .catch((error) => {
        console.error("There has been a problem with your fetch operation:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, setdataGroup, setProjectId, setSelectedClient, setSelectedProject, setSdataGroup, setBanners, setselectedBanner, setWeightingData, setSavedWeightings, setWeightingStats, setcurrentSelectedVariablesset, setSelectedProjectDetails, setOriginalDataRows, setPowner]);

  useEffect(() => {
  if (sortedProjects.length > 0 && !selectedProjectId) {
    handleProjectClick(sortedProjects[0]);
  }
}, [sortedProjects, selectedProjectId, handleProjectClick]);

  const handleCreateProjectClick = useCallback(async () => {
    if (!showForm) {
      await fetchClientsProjects();
    }
    setShowForm(!showForm);

    if (showForm) {
      setSelectedClient(null);
      setSelectedProject(null);
      setClientSearchTerm("");
      setProjectSearchTerm("");
      setFile(null);
      setQnrFile(null);
      setExcelFile(null);
    }
  }, [showForm, fetchClientsProjects]);

  const filteredClients = useMemo(() => {
    return clients.filter((client) =>
      client.name.toLowerCase().includes(debouncedClientSearch.toLowerCase())
    );
  }, [clients, debouncedClientSearch]);

  const filteredProjectsList = useMemo(() => {
    return projects.filter((project) =>
      project.name.toLowerCase().includes(debouncedProjectSearch.toLowerCase())
    );
  }, [projects, debouncedProjectSearch]);

  const clearSearch = useCallback(() => {
    setProjectSearchQuery("");
  }, []);

  if (!user || !user.user || !user.user.projects) {
    return <div className="home-loading">Loading...</div>;
  }

  return (
    <div className="home-container">
      <div className="home-sidebar">
        <div className="home-sidebar-header">
          <h2>My Projects</h2>
          <button
            className={`home-refresh-btn-icon ${isRefreshing ? 'refreshing' : ''}`}
            onClick={refreshUserProjects}
            title="Refresh Projects"
            disabled={isRefreshing}
          >
            {isRefreshing ? <FaSpinner className="spinning" /> : <FaSync />}
          </button>
        </div>

        {/* Search input for projects - Enhanced placeholder */}
        <div className="home-project-search-wrapper">
          <FaSearch className="home-search-icon" />
          <input
            type="text"
            placeholder="Search by name, client, owner or ID..."
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            className="home-project-search-input"
          />
          {projectSearchQuery && (
            <button className="home-clear-search-btn" onClick={clearSearch}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* Search result count */}
        {debouncedProjectQuery && (
          <div className="home-search-result-count">
            Found {sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''}
          </div>
        )}

        <div className="home-project-list">
          {sortedProjects.length > 0 ? (
            sortedProjects.map((project) => (
              <ProjectButton
                key={project.id}
                project={project}
                isSelected={selectedProjectId === project.id}
                onClick={() => handleProjectClick(project)}
              />
            ))
          ) : (
            <div className="home-no-projects-found">
              {debouncedProjectQuery ? "No projects match your search" : "No projects found"}
            </div>
          )}
        </div>
      </div>

      <div className="home-content">
        {loading ? (
          <Loader loading={true} />
        ) : isProcessingSurvey ? (
          <SurveyLoader loading={true} messages={[]} />
        ) : (
          <>
            <h1>Welcome to the Dashboard</h1>
            <div className="home-content-header">
              <p>Select a project or create a new one to get started.</p>
              <button
                className="home-create-project-btn"
                onClick={handleCreateProjectClick}
              >
                {showForm ? "Close" : "Create New Project"}
              </button>
            </div>
            {showForm && (
              <form className="home-project-form" onSubmit={(e) => e.preventDefault()}>
                <div className="home-form-group">
                  <label htmlFor="clientName">Client Name</label>
                  <div className="home-autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Type or select a client"
                      value={clientSearchTerm}
                      onChange={(e) => {
                        setClientSearchTerm(e.target.value);
                        setShowClientDropdown(true);
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      className="home-autocomplete-input"
                    />
                    {showClientDropdown && (
                      <div className="home-autocomplete-dropdown">
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="home-autocomplete-item"
                            onClick={() => {
                              setSelectedClient(client);
                              setClientSearchTerm(client.name);
                              setShowClientDropdown(false);
                              getProjectList(client.id);
                            }}
                          >
                            {client.name}
                          </div>
                        ))}
                        {clientSearchTerm &&
                          !clients.some(
                            (c) =>
                              c.name.toLowerCase() === clientSearchTerm.toLowerCase()
                          ) && (
                            <div
                              className="home-autocomplete-item create-new"
                              onClick={() => {
                                setSelectedClient({ id: null, name: clientSearchTerm });
                                setShowClientDropdown(false);
                              }}
                            >
                              + Create new: "{clientSearchTerm}"
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="home-form-group">
                  <label htmlFor="projectName">Project Name</label>
                  <div className="home-autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Type or select a project"
                      value={projectSearchTerm}
                      onChange={(e) => {
                        setProjectSearchTerm(e.target.value);
                        setShowProjectDropdown(true);
                      }}
                      onFocus={() => setShowProjectDropdown(true)}
                      disabled={!selectedClient?.name}
                      className="home-autocomplete-input"
                    />
                    {showProjectDropdown && selectedClient?.name && (
                      <div className="home-autocomplete-dropdown">
                        {filteredProjectsList.map((project) => (
                          <div
                            key={project.id}
                            className="home-autocomplete-item"
                            onClick={() => {
                              setSelectedProject(project);
                              setProjectSearchTerm(project.name);
                              setShowProjectDropdown(false);
                            }}
                          >
                            {project.name}
                          </div>
                        ))}
                        {projectSearchTerm &&
                          !projects.some(
                            (p) =>
                              p.name.toLowerCase() === projectSearchTerm.toLowerCase()
                          ) && (
                            <div
                              className="home-autocomplete-item create-new"
                              onClick={() => {
                                setSelectedProject({ id: null, name: projectSearchTerm });
                                setShowProjectDropdown(false);
                              }}
                            >
                              + Create new: "{projectSearchTerm}"
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="home-form-group">
                  <label htmlFor="file">Upload File</label>
                  <input
                    type="file"
                    id="file"
                    accept=".sav,.pdf,.docx,.doc,.xls,.xlsx"
                    onChange={handleFileChange}
                    required
                  />
                </div>

                {isSavFile && (
                  <>
                    <div className="home-form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeExtraData}
                          onChange={(e) => setIncludeExtraData(e.target.checked)}
                        />
                        Save Data
                      </label>
                    </div>

                    <div className="home-form-group">
                      <label htmlFor="qnrFile">Upload Questionnaire (Optional - for AI Validation)</label>
                      <input
                        type="file"
                        id="qnrFile"
                        accept=".docx,.doc,.xlsx,.xls,.pdf"
                        onChange={handleQnrFileChange}
                      />
                      {qnrFile && <span className="home-file-info">✓ {qnrFile.name}</span>}
                      <p style={{ fontSize: "0.8em", color: "#666", marginTop: "4px" }}>
                        Uploading a QNR file will automatically validate and update your SPSS variables.
                      </p>
                    </div>
                  </>
                )}

                <div className="home-form-group">
                  <label htmlFor="excel">Upload Excel Data (Optional)</label>
                  <input
                    type="file"
                    id="excel"
                    accept=".xlsx,.xls"
                    onChange={handleExcelChange}
                  />
                  {excelFile && <span className="home-file-info">✓ {excelFile.name}</span>}
                </div>

                <button
                  type="button"
                  className="home-submit-btn"
                  onClick={() => {
                    if (!file) {
                      showToastMessage("Please select a file before uploading.");
                      return;
                    }
                    if (isUploading) return;

                    if (file.name.toLowerCase().endsWith(".sav")) {
                      handleUpload();
                    } else {
                      handleSurvey();
                    }
                  }}
                  disabled={isUploading || loading}
                >
                  {isUploading ? "Processing..." : "Submit"}
                </button>
              </form>
            )}
            {!showForm && (
              <ProjectDetails
                project={selectedProjectDetails}
                projectid={projectId}
                projectOwner={powner}
              />
            )}
          </>
        )}
      </div>

      {showModelDialog && (
        <div className="home-model-overlay" onClick={() => setShowModelDialog(false)}>
          <div className="home-model-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select AI Model</h3>
            <p>Choose which model to use for survey processing:</p>
            <div className="home-model-options">
              <label className="home-model-option">
                <input
                  type="radio"
                  name="model"
                  value="gemini"
                  checked={selectedModel === "gemini"}
                  onChange={(e) => setSelectedModel(e.target.value)}
                />
                <span>Model EraAI (excel file also work)</span>
              </label>
              <label className="home-model-option">
                <input
                  type="radio"
                  name="model"
                  value="openai"
                  checked={selectedModel === "openai"}
                  onChange={(e) => setSelectedModel(e.target.value)}
                />
                <span>Model Azure</span>
              </label>
              <label className="home-model-option">
                <input
                  type="radio"
                  name="model"
                  value="llama"
                  checked={selectedModel === "llama"}
                  onChange={(e) => setSelectedModel(e.target.value)}
                />
                <span>Model Llama</span>
              </label>
            </div>
            <div className="home-modal-actions">
              <button className="home-cancel-btn" onClick={() => setShowModelDialog(false)}>
                Cancel
              </button>
              <button
                className="home-confirm-btn"
                onClick={() => {
                  setShowModelDialog(false);
                  handleSurvey();
                }}
              >
                Process Survey
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;